/**
 * Pulse Report Templates Index
 * Pre-built pulse report templates based on best practices from
 * Stripe, HubSpot, Intercom, Mixpanel, Datadog, and other billion-dollar companies
 *
 * 40+ templates covering:
 * - Billing & Invoice Reports
 * - Usage & Consumption Reports
 * - Analytics & Traffic Reports
 * - Performance & KPI Reports
 * - Digest & Summary Reports
 * - Newsletter & Communications
 * - Product Updates
 * - Custom Reports
 * - Milestone & Growth Reports
 */

// Import all template collections
import { BILLING_TEMPLATES } from "./billing";
import { USAGE_TEMPLATES } from "./usage";
import { ANALYTICS_TEMPLATES } from "./analytics";
import { PERFORMANCE_TEMPLATES } from "./performance";
import { TEAM_TEMPLATES } from "./team";
import { MILESTONE_TEMPLATES } from "./milestone";
import { DIGEST_TEMPLATES } from "./digest";
import { NEWSLETTER_TEMPLATES } from "./newsletter";
import { PRODUCT_UPDATE_TEMPLATES } from "./productUpdate";
import { CUSTOM_TEMPLATES } from "./custom";

// Re-export types
export * from "./types";

// Re-export individual collections
export { BILLING_TEMPLATES } from "./billing";
export { USAGE_TEMPLATES } from "./usage";
export { ANALYTICS_TEMPLATES } from "./analytics";
export { PERFORMANCE_TEMPLATES } from "./performance";
export { TEAM_TEMPLATES } from "./team";
export { MILESTONE_TEMPLATES } from "./milestone";
export { DIGEST_TEMPLATES } from "./digest";
export { NEWSLETTER_TEMPLATES } from "./newsletter";
export { PRODUCT_UPDATE_TEMPLATES } from "./productUpdate";
export { CUSTOM_TEMPLATES } from "./custom";

// Combined templates array
export const ALL_PULSE_REPORT_TEMPLATES = [
  ...BILLING_TEMPLATES,
  ...USAGE_TEMPLATES,
  ...ANALYTICS_TEMPLATES,
  ...PERFORMANCE_TEMPLATES,
  ...DIGEST_TEMPLATES,
  ...NEWSLETTER_TEMPLATES,
  ...PRODUCT_UPDATE_TEMPLATES,
  ...CUSTOM_TEMPLATES,
  ...MILESTONE_TEMPLATES,
];

// Template categories for the gallery
export const PULSE_TEMPLATE_CATEGORIES = [
  {
    id: "invoice",
    name: "Billing & Invoice",
    description: "Payment confirmations, invoices, and billing alerts",
    icon: "DollarOutlined",
    color: "#10b981",
    templates: BILLING_TEMPLATES,
  },
  {
    id: "usage",
    name: "Usage Reports",
    description: "Track consumption, limits, and usage patterns",
    icon: "BarChartOutlined",
    color: "#0ea5e9",
    templates: USAGE_TEMPLATES,
  },
  {
    id: "performance",
    name: "Performance",
    description: "Chatbot metrics, KPIs, and satisfaction scores",
    icon: "DashboardOutlined",
    color: "#f59e0b",
    templates: PERFORMANCE_TEMPLATES,
  },
  {
    id: "analytics",
    name: "Analytics",
    description: "Traffic, engagement, and business insights",
    icon: "LineChartOutlined",
    color: "#8b5cf6",
    templates: ANALYTICS_TEMPLATES,
  },
  {
    id: "digest",
    name: "Digests",
    description: "Weekly, monthly, and quarterly summaries",
    icon: "CalendarOutlined",
    color: "#6366f1",
    templates: DIGEST_TEMPLATES,
  },
  {
    id: "newsletter",
    name: "Newsletters",
    description: "Product tips, industry insights, and community updates",
    icon: "MailOutlined",
    color: "#ec4899",
    templates: NEWSLETTER_TEMPLATES,
  },
  {
    id: "product_update",
    name: "Product Updates",
    description: "Feature releases, improvements, and changelogs",
    icon: "RocketOutlined",
    color: "#14b8a6",
    templates: PRODUCT_UPDATE_TEMPLATES,
  },
  {
    id: "custom",
    name: "Custom Reports",
    description: "ROI, leads, onboarding, and specialized reports",
    icon: "AppstoreOutlined",
    color: "#f97316",
    templates: CUSTOM_TEMPLATES,
  },
  {
    id: "milestone",
    name: "Milestones",
    description: "Achievements, health scores, and growth tracking",
    icon: "TrophyOutlined",
    color: "#a855f7",
    templates: MILESTONE_TEMPLATES,
  },
];

// Helper function to get template by ID
export const getPulseTemplateById = (id: string) => {
  return ALL_PULSE_REPORT_TEMPLATES.find((template) => template.id === id);
};

// Helper function to get templates by report type
export const getPulseTemplatesByReportType = (reportType: string) => {
  return ALL_PULSE_REPORT_TEMPLATES.filter(
    (template) => template.reportType === reportType
  );
};

// Helper function to get templates by tag
export const getPulseTemplatesByTag = (tag: string) => {
  return ALL_PULSE_REPORT_TEMPLATES.filter((template) =>
    template.tags.includes(tag)
  );
};

// Helper function to get templates by difficulty
export const getPulseTemplatesByDifficulty = (
  difficulty: "beginner" | "intermediate" | "advanced"
) => {
  return ALL_PULSE_REPORT_TEMPLATES.filter(
    (template) => template.difficulty === difficulty
  );
};

// Helper function to get templates by schedule type
export const getPulseTemplatesByScheduleType = (scheduleType: string) => {
  return ALL_PULSE_REPORT_TEMPLATES.filter(
    (template) => template.defaultSchedule.type === scheduleType
  );
};
